import {
  mioAgentActionSchema,
  mioAgentStateSchema,
  mioLearnerProfileSchema,
  mioMemorySchema,
  mioTurnPhaseSchema,
} from '@mio/shared';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { notifyUsageError } from '@/shared/api/usage-events';

import { applyMioEvent } from './agent-events.service';
import { createMioAgentClient, type MioAgentClient } from './api/mio-agent.client';
import { captureLessonContext, contextFingerprint, createTabId } from './live-context.service';
import { MioStateContext, type MioState } from './mio-context';
import { mioSuggestions } from './mio-suggestions';
import {
  VoiceSession,
  type VoiceDiagnostics,
  type VoiceStatus,
} from './voice/voice-session.service';

import type { PlaybackSnapshot } from './voice/pcm.service';
import type {
  MioAgentAction,
  MioAgentEvent,
  MioAgentMessage,
  MioAgentState,
  MioContext,
  MioLiveContext,
  MioMemory,
  MioTurnPhase,
} from '@mio/shared';

const unavailable = { chat: false, voice: false, research: false, voiceConfigured: false };
const defaultProfile = mioLearnerProfileSchema.parse({});
const contextUpdateError =
  'Mio could not update your current view. Reconnect before asking about this screen.';

/** Account identity comes from App's auth boundary; route changes never replace this session. */
export function MioProvider({
  children,
  userId,
  client,
}: {
  children: ReactNode;
  userId: string | null;
  client?: MioAgentClient;
}) {
  return (
    <MioSession key={userId ?? 'anonymous'} userId={userId} {...(client ? { client } : {})}>
      {children}
    </MioSession>
  );
}

function MioSession({
  children,
  userId,
  client,
}: {
  children: ReactNode;
  userId: string | null;
  client?: MioAgentClient;
}) {
  const api = useMemo(() => client ?? createMioAgentClient(), [client]);
  const [tabId] = useState(createTabId);
  const [open, setOpenState] = useState(() => {
    try {
      const saved = localStorage.getItem('mio.rail-open');
      if (saved !== null) return saved === 'true';
    } catch {
      /* session preference only */
    }
    return window.matchMedia('(min-width: 1024px)').matches;
  });
  const [messages, setMessages] = useState<MioAgentMessage[]>([]);
  const [memory, setMemory] = useState<MioMemory[]>([]);
  const [profile, setProfile] = useState(defaultProfile);
  const currentProfile = useRef(profile);
  currentProfile.current = profile;
  const [capabilities, setCapabilities] = useState(unavailable);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [phase, setPhase] = useState<MioTurnPhase | null>(null);
  const [loading, setLoading] = useState(Boolean(userId));
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revision, reload] = useState(0);
  const [context, setContext] = useState<MioLiveContext | null>(null);
  const [voiceStatus, setVoiceStatus] = useState<VoiceStatus>('idle');
  const [voiceCaption, setVoiceCaption] = useState('');
  const [voiceDiagnostics, setVoiceDiagnostics] = useState<VoiceDiagnostics | null>(null);
  const [microphoneId, setMicrophoneId] = useState('');
  const [muted, setMuted] = useState(false);
  const [voices, setVoices] = useState<MioState['voices']>([]);
  const live = useRef<MioLiveContext | null>(null);
  const activeTurn = useRef<string | null>(null);
  const activeRequest = useRef<AbortController | null>(null);
  const activeRequestId = useRef<string | null>(null);
  const activeVoiceSessionId = useRef<string | null>(null);
  const cancelledRequests = useRef(new Set<string>());
  const owned = useRef(new Set<string>());
  const cancelled = useRef(new Set<string>());
  const rejectedAudio = useRef(new Set<string>());
  const seen = useRef(new Set<string>());
  const cursor = useRef('');
  const executingAction = useRef(false);
  const deferredContext = useRef<MioLiveContext | null>(null);
  const handler = useRef<((action: MioAgentAction, signal: AbortSignal) => Promise<void>) | null>(
    null,
  );
  const currentAction = useRef<AbortController | null>(null);
  // Messages typed while a turn is running wait their turn instead of interrupting it.
  const queue = useRef<{ requestId: string; message: string; modality: 'chat' | 'voice' }[]>([]);
  const flushQueue = useRef<() => void>(() => undefined);
  const currentThread = useRef(threadId);
  currentThread.current = threadId;
  const alive = useRef(true);
  const sendRef = useRef<(text: string, modality: 'chat' | 'voice') => Promise<void>>(() =>
    Promise.resolve(),
  );
  const cancelRef = useRef<(playback?: PlaybackSnapshot) => void>(() => undefined);
  const voice = useMemo(
    () =>
      new VoiceSession(api, {
        status: setVoiceStatus,
        caption: setVoiceCaption,
        diagnostics: setVoiceDiagnostics,
        turn: (text) => {
          void sendRef.current(text, 'voice');
        },
        interrupt: (playback) => cancelRef.current(playback),
        error: setError,
        context: () => live.current,
      }),
    [api],
  );

  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    try {
      localStorage.setItem('mio.rail-open', String(next));
    } catch {
      /* session only */
    }
  }, []);

  const observeContext = useCallback(
    (observation: MioLiveContext) => {
      if (executingAction.current) {
        deferredContext.current = observation;
        return;
      }
      if (live.current && contextFingerprint(live.current) === contextFingerprint(observation))
        return;
      const next = {
        ...observation,
        tabId,
        revision: (live.current?.revision ?? 0) + 1,
        observedAt: new Date().toISOString(),
      };
      live.current = next;
      setContext(next);
    },
    [tabId],
  );

  const installState = useCallback((state: MioAgentState) => {
    setThreadId(state.threadId);
    currentThread.current = state.threadId;
    setMessages(state.messages);
    setMemory(state.memory);
    setProfile(state.profile);
    setCapabilities(state.capabilities);
    cursor.current = state.lastEventId;
  }, []);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    const abort = new AbortController();
    setLoading(true);
    setError(null);
    void api
      .state(abort.signal)
      .then((state) => {
        if (!abort.signal.aborted) installState(state);
      })
      .catch((cause: unknown) => {
        if (!abort.signal.aborted)
          setError(cause instanceof Error ? cause.message : 'Could not load Mio.');
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
    return () => abort.abort();
  }, [api, installState, userId, revision]);

  useEffect(() => {
    if (!threadId || !context) return;
    let current = true;
    const timer = setTimeout(() => {
      if (executingAction.current) return;
      void api
        .context(threadId, context)
        .then(() => {
          if (current && alive.current)
            setError((previous) => (previous === contextUpdateError ? null : previous));
        })
        .catch(() => {
          if (current && alive.current) setError((previous) => previous ?? contextUpdateError);
        });
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [api, context, threadId]);

  const cancel = useCallback(
    (playback?: PlaybackSnapshot) => {
      currentAction.current?.abort();
      currentAction.current = null;
      executingAction.current = false;
      if (deferredContext.current) {
        const next = deferredContext.current;
        deferredContext.current = null;
        observeContext(next);
      }
      const turnId = activeTurn.current ?? voice.currentTurn;
      const progress = playback ?? voice.interrupt();
      if (playback) voice.interrupt();
      if (activeRequestId.current) {
        cancelledRequests.current.add(activeRequestId.current);
        if (cancelledRequests.current.size > 1000)
          cancelledRequests.current.delete(cancelledRequests.current.values().next().value ?? '');
      }
      activeRequest.current?.abort();
      activeRequest.current = null;
      activeTurn.current = null;
      activeRequestId.current = null;
      activeVoiceSessionId.current = null;
      setPending(false);
      // Stopping a reply moves on to the next waiting question, unless the caller starts
      // its own turn synchronously (a spoken interruption), which the flush then sees.
      setTimeout(() => flushQueue.current(), 0);
      if (turnId) {
        cancelled.current.add(turnId);
        setMessages((current) =>
          current.map((message) =>
            message.turnId === turnId && message.role === 'mio'
              ? { ...message, status: 'interrupted' }
              : message,
          ),
        );
        void api.cancel(turnId, progress).catch(() => {
          if (alive.current)
            setError('Playback stopped. Mio could not confirm server cancellation.');
        });
      }
    },
    [api, observeContext, voice],
  );
  cancelRef.current = cancel;

  const performAction = useCallback(
    async (event: MioAgentEvent) => {
      const parsed = mioAgentActionSchema.safeParse(event.payload.action);
      if (!parsed.success || !event.turnId || !currentThread.current || !live.current) return;
      const action = parsed.data;
      if (action.tabId !== tabId) return;
      const observed = captureLessonContext(live.current, tabId, live.current.revision);
      const stale =
        contextFingerprint(observed) !== contextFingerprint(live.current) ||
        action.contextRevision !== live.current.revision ||
        cancelled.current.has(event.turnId) ||
        executingAction.current;
      let status: 'rendered' | 'rejected' | 'failed' = stale ? 'rejected' : 'rendered';
      let failure: string | undefined;
      const controller = new AbortController();
      if (!stale) {
        executingAction.current = true;
        currentAction.current = controller;
        try {
          if (!handler.current) throw new Error('The current screen cannot perform this action.');
          setOpen(true);
          await handler.current(action, controller.signal);
          if (controller.signal.aborted || cancelled.current.has(event.turnId)) status = 'rejected';
        } catch (cause) {
          status = controller.signal.aborted ? 'rejected' : 'failed';
          failure =
            cause instanceof Error ? cause.message.slice(0, 500) : 'Action could not render.';
        }
      }
      await api
        .acknowledge(action.id, {
          threadId: currentThread.current,
          turnId: event.turnId,
          tabId,
          contextRevision: action.contextRevision,
          status,
          ...(failure ? { error: failure } : {}),
        })
        .catch(() => {
          if (alive.current) setError('Mio could not confirm that the visual is ready.');
        });
      if (!stale && currentAction.current === controller) {
        currentAction.current = null;
        executingAction.current = false;
        if (deferredContext.current) {
          const next = deferredContext.current;
          deferredContext.current = null;
          observeContext(next);
        }
      }
    },
    [api, observeContext, setOpen, tabId],
  );

  const receive = useCallback(
    (event: MioAgentEvent) => {
      if (event.threadId !== currentThread.current) return;
      if (event.type === 'state.reset') {
        const parsed = mioAgentStateSchema.safeParse(event.payload.state);
        if (parsed.success) installState(parsed.data);
        return;
      }
      if (seen.current.has(event.id)) return;
      if (
        /^\d+$/.test(event.id) &&
        /^\d+$/.test(cursor.current) &&
        Number(event.id) <= Number(cursor.current)
      )
        return;
      cursor.current = event.id;
      seen.current.add(event.id);
      if (seen.current.size > 2000) seen.current.delete(seen.current.values().next().value ?? '');
      const speechTurn = voice.currentTurn;
      if (event.type === 'turn.completed' && event.turnId) voice.completeTurn(event.turnId);
      if (event.type === 'turn.failed')
        notifyUsageError(typeof event.payload.code === 'string' ? event.payload.code : undefined);
      if ((event.type === 'turn.cancelled' || event.type === 'turn.failed') && event.turnId)
        rejectedAudio.current.add(event.turnId);
      if (
        speechTurn &&
        (((event.type === 'turn.cancelled' || event.type === 'turn.failed') &&
          event.turnId === speechTurn) ||
          (event.type === 'turn.started' && event.turnId && event.turnId !== speechTurn))
      ) {
        // A server/other-tab cancellation must also stop audio already buffered in this tab.
        // Do not send another cancel: that could target the newer conversation turn.
        owned.current.delete(speechTurn);
        rejectedAudio.current.add(speechTurn);
        voice.interrupt();
      }
      if (
        event.type === 'turn.started' &&
        event.turnId &&
        event.payload.requestId === activeRequestId.current
      ) {
        activeTurn.current = event.turnId;
        owned.current.add(event.turnId);
        if (
          !currentProfile.current.quiet &&
          voice.serverAudio &&
          activeVoiceSessionId.current &&
          activeVoiceSessionId.current === voice.currentSessionId &&
          !cancelled.current.has(event.turnId) &&
          !rejectedAudio.current.has(event.turnId) &&
          !cancelledRequests.current.has(String(event.payload.requestId))
        )
          voice.expectTurn(event.turnId);
      }
      // Aborting a POST does not prove the server never accepted it. Fence its eventual turn too.
      if (
        event.type === 'turn.started' &&
        event.turnId &&
        typeof event.payload.requestId === 'string' &&
        cancelledRequests.current.has(event.payload.requestId)
      ) {
        cancelled.current.add(event.turnId);
        void api.cancel(event.turnId).catch(() => {
          if (alive.current)
            setError('The reply is stopped here, but Mio could not confirm server cancellation.');
        });
      }
      if (event.type === 'text.delta' && event.turnId && cancelled.current.has(event.turnId))
        return;
      if (event.type === 'turn.progress') {
        const parsed = mioTurnPhaseSchema.safeParse(event.payload.phase);
        if (parsed.success && event.turnId && !cancelled.current.has(event.turnId))
          setPhase(parsed.data);
        return;
      }
      if (
        event.type === 'text.delta' ||
        event.type === 'turn.completed' ||
        event.type === 'turn.failed' ||
        event.type === 'turn.cancelled'
      )
        setPhase(null);
      setMessages((current) =>
        applyMioEvent(current, event).map((message) =>
          message.turnId && cancelled.current.has(message.turnId) && message.role === 'mio'
            ? { ...message, status: 'interrupted' }
            : message,
        ),
      );
      if (event.type === 'memory.updated') {
        const parsed = mioMemorySchema.array().safeParse(event.payload.memory);
        if (parsed.success) setMemory(parsed.data);
      }
      if (event.type === 'action.requested') void performAction(event);
      if (
        event.turnId &&
        owned.current.has(event.turnId) &&
        !cancelled.current.has(event.turnId) &&
        !profile.quiet
      ) {
        if (
          !voice.serverAudio &&
          event.type === 'speech.ready' &&
          typeof event.payload.text === 'string'
        )
          voice.append(event.turnId, event.payload.text, event.payload.final !== false);
      }
      if (
        ['turn.completed', 'turn.failed', 'turn.cancelled'].includes(event.type) &&
        event.turnId === activeTurn.current
      ) {
        activeTurn.current = null;
        activeRequestId.current = null;
        setPending(false);
        flushQueue.current();
        if (event.type === 'turn.failed')
          setError(
            event.payload.code === 'usage_limit_exceeded'
              ? 'Your shared AI allowance is used up. Open your plan to see upgrade and renewal options.'
              : typeof event.payload.error === 'string'
                ? event.payload.error
                : 'Mio could not finish this reply. Try again.',
          );
      }
    },
    [api, installState, performAction, profile.quiet, voice],
  );
  const receiveRef = useRef(receive);
  receiveRef.current = receive;

  useEffect(() => {
    if (!threadId) return;
    const abort = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    const connect = async () => {
      if (abort.signal.aborted) return;
      const started = Date.now();
      let clean = false;
      try {
        setConnected(true);
        await api.events(threadId, cursor.current, abort.signal, (event) => {
          attempts = 0;
          receiveRef.current(event);
        });
        clean = true;
      } catch {
        if (!abort.signal.aborted) setConnected(false);
      }
      if (!abort.signal.aborted) {
        // A normal renewable stream should resume with no one-second blind spot.
        // Short empty closes remain failures for backoff, preventing hot loops.
        const renewed = clean && Date.now() - started >= 1000;
        if (renewed) attempts = 0;
        else setConnected(false);
        timer = setTimeout(
          () => {
            void connect();
          },
          renewed ? 0 : Math.min(1000 * 2 ** attempts++, 15_000),
        );
      }
    };
    void connect();
    return () => {
      abort.abort();
      clearTimeout(timer);
      setConnected(false);
    };
  }, [api, threadId, revision]);

  const startTurn = useCallback(
    async (requestId: string, message: string, modality: 'chat' | 'voice') => {
      if (!threadId || !live.current) return;
      const request = new AbortController();
      activeRequest.current = request;
      activeRequestId.current = requestId;
      const voiceSessionId =
        voice.connected && voice.serverAudio && !profile.quiet ? voice.currentSessionId : null;
      activeVoiceSessionId.current = voiceSessionId;
      setError(null);
      setPending(true);
      setMessages((current) =>
        current.map((item) =>
          item.id === requestId && item.status === 'queued'
            ? { ...item, status: 'complete' }
            : item,
        ),
      );
      try {
        const result = await api.turn(
          {
            requestId,
            threadId,
            message,
            modality,
            responseMode:
              (modality === 'voice' || voice.connected) && !profile.quiet ? 'voice' : 'text',
            ...(voiceSessionId ? { voiceSessionId } : {}),
            context: live.current,
          },
          request.signal,
        );
        if (request.signal.aborted) {
          await api.cancel(result.turnId);
          return;
        }
        if (activeRequestId.current === requestId) activeTurn.current = result.turnId;
        owned.current.add(result.turnId);
        if (
          voiceSessionId &&
          voiceSessionId === voice.currentSessionId &&
          !currentProfile.current.quiet &&
          !cancelled.current.has(result.turnId) &&
          !rejectedAudio.current.has(result.turnId)
        )
          voice.expectTurn(result.turnId);
        setMessages((current) =>
          current.map((item) =>
            item.id === requestId ? { ...item, turnId: result.turnId } : item,
          ),
        );
      } catch (cause) {
        if (!request.signal.aborted) {
          if (activeRequestId.current === requestId) activeRequestId.current = null;
          setPending(false);
          setMessages((current) =>
            current.map((item) => (item.id === requestId ? { ...item, status: 'failed' } : item)),
          );
          setError(cause instanceof Error ? cause.message : 'Could not send your message.');
          flushQueue.current();
        }
      }
    },
    [api, profile.quiet, threadId, voice],
  );
  flushQueue.current = () => {
    if (!alive.current || activeRequestId.current || activeTurn.current) return;
    const next = queue.current.shift();
    if (next) void startTurn(next.requestId, next.message, next.modality);
  };

  const send = useCallback(
    async (text: string, page?: MioContext, modality: 'chat' | 'voice' = 'chat') => {
      const message = text.trim();
      if (!message || !threadId || !capabilities.chat) return;
      if (page) observeContext(captureLessonContext(page, tabId, live.current?.revision ?? 0));
      if (!live.current) {
        setError('Wait for the current screen to load before messaging Mio.');
        return;
      }
      const requestId = crypto.randomUUID();
      // A typed follow-up waits for the running reply; a spoken one interrupts it.
      const waiting =
        modality === 'chat' && (activeRequestId.current !== null || activeTurn.current !== null);
      if (waiting && queue.current.length >= 5) {
        setError('Wait for Mio to finish before sending more.');
        return;
      }
      if (!waiting) cancel();
      setMessages((current) => [
        ...current,
        {
          id: requestId,
          role: 'learner',
          content: message,
          createdAt: new Date().toISOString(),
          modality,
          status: waiting ? 'queued' : 'complete',
          artifacts: [],
          sources: [],
        },
      ]);
      if (waiting) {
        queue.current.push({ requestId, message, modality });
        return;
      }
      await startTurn(requestId, message, modality);
    },
    [cancel, capabilities.chat, observeContext, startTurn, tabId, threadId],
  );
  sendRef.current = (text, modality) => send(text, undefined, modality);

  useEffect(() => {
    alive.current = true;
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'j') {
        event.preventDefault();
        setOpenState((value) => !value);
      }
    };
    window.addEventListener('keydown', onKey);
    const interruptAction = (event: Event) => {
      if (event.isTrusted) currentAction.current?.abort();
    };
    for (const name of ['pointerdown', 'wheel', 'touchstart', 'keydown'])
      document.addEventListener(name, interruptAction, { capture: true, passive: true });
    return () => {
      alive.current = false;
      activeRequest.current?.abort();
      currentAction.current?.abort();
      voice.stop();
      window.removeEventListener('keydown', onKey);
      for (const name of ['pointerdown', 'wheel', 'touchstart', 'keydown'])
        document.removeEventListener(name, interruptAction, true);
    };
  }, [voice]);

  const clear = useCallback(async () => {
    queue.current = [];
    cancel();
    voice.stop();
    setLoading(true);
    try {
      installState(await api.newThread());
      seen.current.clear();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not start a new conversation.');
    } finally {
      setLoading(false);
    }
  }, [api, cancel, installState, voice]);

  const actionHandler = useCallback(
    (callback: ((action: MioAgentAction, signal: AbortSignal) => Promise<void>) | null) => {
      handler.current = callback;
    },
    [],
  );

  const value: MioState = {
    open,
    mode: voiceStatus === 'idle' ? 'chat' : 'call',
    tabId,
    messages,
    suggestions: mioSuggestions(context),
    pending,
    phase,
    loading,
    connected,
    error,
    context,
    capabilities,
    memory,
    profile,
    voices,
    voiceStatus,
    voiceCaption,
    voiceDiagnostics,
    microphoneId,
    selectMicrophone: async (deviceId) => {
      setMicrophoneId(deviceId);
      if (voiceStatus !== 'idle' && threadId && capabilities.voice) {
        cancel();
        setError(null);
        setMuted(false);
        await voice.start(threadId, deviceId || undefined);
      }
    },
    muted,
    setOpen,
    toggle: () => setOpen(!open),
    send,
    cancel: () => cancel(),
    clear,
    retry: () => reload((value) => value + 1),
    startCall: async () => {
      setOpen(true);
      setError(null);
      setMuted(false);
      if (threadId && capabilities.voice) await voice.start(threadId, microphoneId || undefined);
    },
    endCall: () => {
      cancel();
      voice.stop();
      setMuted(false);
    },
    toggleMute: () => {
      const next = !muted;
      setMuted(next);
      voice.mute(next);
    },
    observeContext,
    actionHandler,
    saveMemory: async (id, text) => {
      const saved = await api.updateMemory(id, text);
      setMemory((current) => current.map((item) => (item.id === saved.id ? saved : item)));
    },
    removeMemory: async (id) => {
      await api.deleteMemory(id);
      setMemory((current) => current.filter((item) => item.id !== id));
    },
    resetMemory: async () => {
      await api.resetMemory();
      setMemory([]);
    },
    saveProfile: async (patch) => {
      const saved = await api.updateProfile(patch);
      currentProfile.current = saved;
      setProfile(saved);
      if (saved.quiet) voice.interrupt();
      if (patch.voiceId !== undefined) setCapabilities((await api.state()).capabilities);
    },
    loadVoices: async () => {
      setVoices((await api.voices()).voices);
    },
  };
  return <MioStateContext.Provider value={value}>{children}</MioStateContext.Provider>;
}
